import { YonduOAuthInteraction } from '@ver.id/libs.ui.yondu';
import { type JourneyManager } from '../hooks/useJourney';
import { type useParentMessaging } from '../hooks/useParentMessaging';
import { driveEmbeddedFlow, EmbeddedFlowHTTPError } from './driveEmbeddedFlow';
import { buildSession } from './initSession';
import { getEnv } from '../utils/env';
import { isNetworkError } from '../utils/networkError';
import { type AppAction } from './appState';
import { type AbortVariant, type RonanInteractionContext } from '../types';

type ParentMessaging = ReturnType<typeof useParentMessaging>;

/**
 * embedded/request's OAuth errors. The first three are the website's setup
 * (the person sees one generic screen that sends them to the organisation;
 * the page hears the code); anything else is Ver.iD's own failure.
 */
const REQUEST_ERRORS: Record<string, AbortVariant> = {
  unauthorized_client: 'unauthorizedClient',
  invalid_scope: 'invalidScope',
  invalid_request: 'invalidRequest',
  temporarily_unavailable: 'temporarilyUnavailable',
};

function requestErrorVariant(code: string): AbortVariant {
  return REQUEST_ERRORS[code] ?? 'serverError';
}

export function embeddedInit(
  dispatch: React.Dispatch<AppAction>,
  journey: JourneyManager,
  parentMessaging: ParentMessaging,
): (() => void) | void {
  if (parentMessaging.handshakeError) {
    dispatch({ action: 'INIT_ERROR', variant: 'invalidRequest' });
    return;
  }
  if (!parentMessaging.initData) return;

  const initData = parentMessaging.initData;
  const abortController = new AbortController();
  const { signal } = abortController;

  const init = async () => {
    try {
      // window.location.origin is the gateway host in an iframe; the embedded
      // authorization endpoints live on the OAuth-SSI service.
      const baseUrl =
        getEnv('RONAN_OAUTH_SSI_URL').replace(/\/$/, '') ||
        window.location.origin;

      const result = await driveEmbeddedFlow(
        {
          baseUrl,
          clientId: initData.clientId,
          scope: initData.scope,
          state: initData.state,
          codeChallenge: initData.codeChallenge,
          webhookUri: initData.webhookUri,
          intentId: initData.intentId,
        },
        signal,
      );
      if (signal.aborted) return;

      if (result.status === 'error') {
        dispatch({
          action: 'INIT_ERROR',
          variant: requestErrorVariant(result.error),
        });
        return;
      }

      const interaction = YonduOAuthInteraction.fromURIs(result);
      journey.setOAuthInteraction(interaction);

      const ctx = await interaction.start<RonanInteractionContext>();
      if (signal.aborted) return;

      // Verify the parent origin against the server-supplied allowlist.
      const allowedEmbedOrigins =
        ctx.deliveryModeSpecificConfig?.embedded?.allowedEmbedOrigins;
      if (!allowedEmbedOrigins) {
        dispatch({ action: 'INIT_ERROR', variant: 'unauthorizedClient' });
        return;
      }
      if (!parentMessaging.verifyOrigin(allowedEmbedOrigins)) {
        dispatch({ action: 'INIT_ERROR', variant: 'originNotAllowed' });
        return;
      }

      const sessionResult = await buildSession(ctx, { signal });
      if (sessionResult.status === 'aborted') return;
      if (sessionResult.status === 'error') {
        dispatch({ action: 'INIT_ERROR', variant: sessionResult.variant });
        return;
      }

      dispatch({
        action: 'INIT_SUCCESS',
        session: sessionResult.session,
        isMobile: false,
      });
      parentMessaging.notify({ type: 'ronan:ready' });
    } catch (e) {
      if (signal.aborted) return;

      if (e instanceof EmbeddedFlowHTTPError) {
        dispatch({
          action: 'INIT_ERROR',
          variant: e.status === 503 ? 'temporarilyUnavailable' : 'serverError',
        });
        return;
      }

      const isNetwork = isNetworkError(e);
      dispatch({
        action: 'INIT_ERROR',
        variant: isNetwork ? 'connectionLost' : 'serverError',
      });
    }
  };

  init();

  return () => {
    abortController.abort();
  };
}
